import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';

import { endpoints } from '@/api/endpoints';
import { RoomCard, ApplianceCard } from '@/components/ItemCards';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { LoadingState, ErrorState } from '@/components/states';
import type {
  ProjectInput,
  ProvenanceSource,
  ProjectType,
  SupplyType,
  GenerateBoardRequest,
} from '@/types/api';

// ─── Provenance badge ──────────────────────────────────────────────────────

const PROVENANCE_STYLES: Record<ProvenanceSource, { label: string; tone: 'accent' | 'success' | 'warning' }> = {
  ocr:            { label: 'OCR',     tone: 'accent' },
  knowledge_base: { label: 'KB',      tone: 'accent' },
  user:           { label: 'USER',    tone: 'success' },
  derived:        { label: 'DERIVED', tone: 'warning' },
};

function ProvenanceBadge({ source }: { source: ProvenanceSource | undefined }) {
  if (!source) {
    return <Badge tone="neutral" mono>—</Badge>;
  }
  const { label, tone } = PROVENANCE_STYLES[source];
  return <Badge tone={tone} mono>{label}</Badge>;
}

// ─── Schema-free record renderer ───────────────────────────────────────────

function ItemCard({ item, index }: { item: Record<string, unknown>; index: number }) {
  return (
    <div className="border border-border rounded p-3 bg-surface text-xs">
      <div className="font-mono text-ink-4 mb-1">#{index + 1}</div>
      <dl className="space-y-1">
        {Object.entries(item).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="font-mono text-ink-4 min-w-[100px]">{k}:</dt>
            <dd className="text-ink break-all">
              {v === null || v === undefined ? (
                <span className="text-ink-4 italic">null</span>
              ) : typeof v === 'object' ? (
                <code className="text-[11px]">{JSON.stringify(v)}</code>
              ) : (
                String(v)
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ─── Generate form ─────────────────────────────────────────────────────────

interface FormState {
  project_label: string;
  project_type: ProjectType | '';
  supply_type: SupplyType;
  preferred_rcd_strategy: string;
  split_c4: boolean;
  manual_appliances: string;
  notes: string;
  force_dedicated: string;
  exclude_appliances: string;
}

const RCD_STRATEGIES = [
  { value: 'recommended_rebt', labelKey: 'manual.rcd_recommended' },
  { value: 'minimal_rebt', labelKey: 'manual.rcd_minimal' },
  { value: 'comfort_rebt', labelKey: 'manual.rcd_comfort' },
];

function normalizeRcdStrategy(value: string | null | undefined) {
  if (value === 'minimal_rebt' || value === 'recommended_rebt' || value === 'comfort_rebt') {
    return value;
  }
  return 'recommended_rebt';
}

function supportsSplitC4(strategy: string) {
  return strategy === 'recommended_rebt' || strategy === 'comfort_rebt';
}

function GenerateForm({
  project,
  onGenerate,
  isGenerating,
}: {
  project: ProjectInput;
  onGenerate: (req: GenerateBoardRequest) => void;
  isGenerating: boolean;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>({
    project_label: project.project_label ?? '',
    project_type:  (project.project_type as ProjectType | null) ?? '',
    supply_type:   project.supply_type ?? '1ph',
    preferred_rcd_strategy: normalizeRcdStrategy(project.preferred_rcd_strategy),
    split_c4: project.split_c4 ?? true,
    manual_appliances: '',
    notes: '',
    force_dedicated: '',
    exclude_appliances: '',
  });

  const handleSubmit = () => {
    const nullableTrim = (s: string) => {
      const trimmed = s.trim();
      return trimmed || null;
    };
    const nullableProjectType = (value: ProjectType | '') =>
      nullableTrim(value) as ProjectType | null;
    const nullableSupplyType = (value: SupplyType) =>
      nullableTrim(value) as SupplyType | null;
    const splitCsv = (s: string) =>
      s.split(',').map(x => x.trim()).filter(Boolean);

    const req: GenerateBoardRequest = {
      user_input: {
        project_label: nullableTrim(form.project_label),
        project_type:  nullableProjectType(form.project_type),
        supply_type:   nullableSupplyType(form.supply_type),
        preferred_rcd_strategy: nullableTrim(form.preferred_rcd_strategy),
        split_c4: supportsSplitC4(form.preferred_rcd_strategy) ? form.split_c4 : null,
        manual_appliances: splitCsv(form.manual_appliances),
        notes: nullableTrim(form.notes),
      },
      overrides: {
        project_type: nullableProjectType(form.project_type),
        supply_type:  nullableSupplyType(form.supply_type),
        preferred_rcd_strategy: nullableTrim(form.preferred_rcd_strategy),
        split_c4: supportsSplitC4(form.preferred_rcd_strategy) ? form.split_c4 : null,
        force_dedicated_appliances: splitCsv(form.force_dedicated),
        exclude_appliances: splitCsv(form.exclude_appliances),
      },
    };
    onGenerate(req);
  };

  return (
    <div className="border border-border rounded-lg p-6 bg-surface-2">
      <h3 className="font-sans text-sm font-semibold text-ink-3 mb-4">
        {t('project.form.title')}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t('project.form.label')}>
          <Input
            type="text"
            value={form.project_label}
            onChange={e => setForm({ ...form, project_label: e.target.value })}
          />
        </Field>

        <Field label={t('project.form.project_type')}>
          <Select
            value={form.project_type}
            onChange={e => setForm({ ...form, project_type: e.target.value as ProjectType | '' })}
          >
            <option value="">—</option>
            <option value="apartment">apartment</option>
            <option value="house">house</option>
          </Select>
        </Field>

        <Field label={t('project.form.supply_type')}>
          <Select
            value={form.supply_type}
            onChange={e => setForm({ ...form, supply_type: e.target.value as SupplyType })}
          >
            <option value="1ph">{t('project.form.supply_1ph')}</option>
            <option value="2ph">{t('project.form.supply_2ph')}</option>
          </Select>
        </Field>

        <Field label={t('project.form.rcd_strategy')}>
          <Select
            value={form.preferred_rcd_strategy}
            onChange={e => {
              const next = e.target.value;
              setForm({ ...form, preferred_rcd_strategy: next, split_c4: supportsSplitC4(next) ? true : form.split_c4 });
            }}
          >
            {RCD_STRATEGIES.map(s => <option key={s.value} value={s.value}>{t(s.labelKey)}</option>)}
          </Select>
        </Field>

        {supportsSplitC4(form.preferred_rcd_strategy) && (
          <label className="col-span-2 flex items-center gap-2 text-sm text-ink-3">
            <input
              type="checkbox"
              checked={form.split_c4}
              onChange={e => setForm({ ...form, split_c4: e.target.checked })}
              className="h-4 w-4 rounded border-border-2 bg-surface"
              style={{ accentColor: 'rgb(var(--accent))' }}
            />
            {t('manual.split_appliances_by_lines')}
          </label>
        )}

        <div className="col-span-2">
          <Field label={t('project.form.manual_appliances')} hint={t('project.form.csv_hint')}>
            <Input
              type="text"
              placeholder="oven, washer, dishwasher"
              value={form.manual_appliances}
              onChange={e => setForm({ ...form, manual_appliances: e.target.value })}
              className="font-mono"
            />
          </Field>
        </div>

        <Field label={t('project.form.force_dedicated')}>
          <Input
            type="text"
            placeholder="washer"
            value={form.force_dedicated}
            onChange={e => setForm({ ...form, force_dedicated: e.target.value })}
            className="font-mono"
          />
        </Field>

        <Field label={t('project.form.exclude_appliances')}>
          <Input
            type="text"
            value={form.exclude_appliances}
            onChange={e => setForm({ ...form, exclude_appliances: e.target.value })}
            className="font-mono"
          />
        </Field>

        <div className="col-span-2">
          <Field label={t('project.form.notes')}>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </Field>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          variant="primary"
          type="button"
          onClick={handleSubmit}
          disabled={isGenerating}
        >
          {isGenerating ? t('project.generating') : `${t('project.generate')} →`}
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

export function ProjectPage() {
  const { id: idStr } = useParams();
  const id = Number.parseInt(idStr ?? '', 10);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const debug = searchParams.get('debug') === '1';

  const projectQuery = useQuery({
    queryKey: ['project', id],
    queryFn: () => endpoints.getProject(id),
    enabled: Number.isFinite(id),
  });

  const generateMutation = useMutation({
    mutationFn: (req: GenerateBoardRequest) => endpoints.generateBoard(id, req),
    onSuccess: (data) => {
      navigate(`/designs/${data.design_id}`);
    },
  });

  if (!Number.isFinite(id)) {
    return <div className="text-danger">{t('errors.not_found')}</div>;
  }

  return (
    <div className="animate-fade-up">
      <Link to="/projects" className="text-xs font-medium text-ink-3 hover:text-accent
                              transition-colors mb-4 inline-block">
        ← {t('nav.dashboard')}
      </Link>

      {projectQuery.isLoading && <LoadingState />}

      {projectQuery.isError && (
        <ErrorState
          error={projectQuery.error}
          onRetry={() => void projectQuery.refetch()}
        />
      )}

      {projectQuery.isSuccess && (
        <ProjectContent
          project={projectQuery.data}
          projectId={id}
          debug={debug}
          onGenerate={generateMutation.mutate}
          isGenerating={generateMutation.isPending}
          generateError={generateMutation.error}
        />
      )}
    </div>
  );
}

// ─── Project content (когда snapshot загружен) ──────────────────────────────

function ProjectContent({
  project,
  projectId,
  debug,
  onGenerate,
  isGenerating,
  generateError,
}: {
  project: ProjectInput;
  projectId: number;
  debug: boolean;
  onGenerate: (req: GenerateBoardRequest) => void;
  isGenerating: boolean;
  generateError: unknown;
}) {
  const { t } = useTranslation();
  const roomLabels = {
    lighting: t('project.room_lighting'),
    sockets: t('project.room_sockets'),
    socketPower: t('project.room_socket_power'),
    watt: t('project.unit_watt'),
    fallbackName: t('project.room_fallback'),
  };
  const applianceLabels = {
    watt: t('project.unit_watt'),
    fallbackName: t('project.appliance_fallback'),
  };

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-baseline gap-3 mb-1">
          <h1 className="font-sans text-xl font-semibold tracking-[-0.015em]">
            {project.project_label || `Project #${projectId}`}
          </h1>
          <span className="font-mono text-xs text-ink-4">#{projectId}</span>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          {project.project_type && (
            <span className="text-ink-3">
              type: <span className="font-mono text-ink">{project.project_type}</span>
              <ProvenanceBadge source={project.provenance?.project_type} />
            </span>
          )}
          <span className="text-ink-3">
            supply: <span className="font-mono text-ink">{project.supply_type}</span>
            <ProvenanceBadge source={project.provenance?.supply_type} />
          </span>
          <span className="text-ink-3">
            rooms: <span className="font-mono text-ink">{project.rooms.length}</span>
          </span>
          <span className="text-ink-3">
            appliances: <span className="font-mono text-ink">{project.appliances.length}</span>
          </span>
        </div>
      </div>

      {/* Missing required */}
      {project.missing_required.length > 0 && (
        <div className="mb-6 border border-warning/40 bg-warning/5 rounded-lg p-4">
          <div className="font-sans text-sm font-semibold text-warning mb-2">
            {t('project.missing_required')} ({project.missing_required.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {project.missing_required.map(field => (
              <span key={field} className="font-mono text-xs px-2 py-1 rounded
                                            bg-warning/10 text-warning">
                {field}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Two-column layout: rooms+appliances | form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Section title={`${t('project.rooms')} (${project.rooms.length})`}>
            {project.rooms.length === 0 ? (
              <div className="text-xs text-ink-4 italic">{t('project.empty')}</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {project.rooms.map((r, i) => (
                  debug
                    ? <ItemCard key={i} item={r} index={i} />
                    : <RoomCard key={i} room={r} index={i} labels={roomLabels} />
                ))}
              </div>
            )}
          </Section>

          <Section title={`${t('project.appliances')} (${project.appliances.length})`}>
            {project.appliances.length === 0 ? (
              <div className="text-xs text-ink-4 italic">{t('project.empty')}</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {project.appliances.map((a, i) => (
                  debug
                    ? <ItemCard key={i} item={a} index={i} />
                    : <ApplianceCard key={i} appliance={a} index={i} labels={applianceLabels} />
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-6">
          <GenerateForm
            project={project}
            onGenerate={onGenerate}
            isGenerating={isGenerating}
          />

          {generateError ? <ErrorState error={generateError} /> : null}
        </div>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}
